import { componentOutletConfigFactory } from '@core/common-legacy';
import type { ClientToolColumn, ClientToolFormatHint } from '@core/tellent-assistant-types';
import type {
  DataTableRow,
  NewDataTableColumn,
  TableCellFactory,
} from '@core/user-interface-legacy';

import { AssistantCellBooleanComponent } from './cells/cell-boolean.component';
import { AssistantCellDateComponent } from './cells/cell-date.component';
import { AssistantCellDatetimeComponent } from './cells/cell-datetime.component';
import { AssistantCellNumberComponent } from './cells/cell-number.component';
import { AssistantCellPersonComponent } from './cells/cell-person.component';
import { AssistantCellStatusComponent } from './cells/cell-status.component';
import { AssistantCellTextComponent } from './cells/cell-text.component';

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface AssistantTableRow extends DataTableRow {
  readonly _src: Record<string, unknown>;
}

export const rowId = (src: Record<string, unknown>, fallback: number): number | string => {
  const id = src['id'];
  return typeof id === 'string' || typeof id === 'number' ? id : fallback;
};

export const inferFormat = (
  key: string,
  rows: ReadonlyArray<Record<string, unknown>>,
): ClientToolFormatHint => {
  const sample = rows.find(r => r[key] != null)?.[key];
  if (typeof sample === 'boolean') return 'boolean';
  if (typeof sample === 'number') return 'number';
  if (typeof sample === 'string') {
    if (ISO_DATETIME_RE.test(sample)) return 'datetime';
    if (ISO_DATE_RE.test(sample)) return 'date';
  }
  return 'text';
};

export const compareValues = (a: unknown, b: unknown): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
};

const parseWidth = (raw: string | undefined): number | 'auto' => {
  if (!raw) return 'auto';
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 'auto';
};

const cellFactoryFor = (
  col: ClientToolColumn,
  format: ClientToolFormatHint,
): TableCellFactory<AssistantTableRow> => {
  switch (format) {
    case 'date':
      return ({ row }) =>
        componentOutletConfigFactory(AssistantCellDateComponent, {
          inputs: { value: row._src[col.key] },
        });
    case 'datetime':
      return ({ row }) =>
        componentOutletConfigFactory(AssistantCellDatetimeComponent, {
          inputs: { value: row._src[col.key] },
        });
    case 'number':
      return ({ row }) =>
        componentOutletConfigFactory(AssistantCellNumberComponent, {
          inputs: { value: row._src[col.key] },
        });
    case 'status':
      return ({ row }) =>
        componentOutletConfigFactory(AssistantCellStatusComponent, {
          inputs: { value: row._src[col.key], statusMap: col.statusMap ?? undefined },
        });
    case 'boolean':
      return ({ row }) =>
        componentOutletConfigFactory(AssistantCellBooleanComponent, {
          inputs: { value: row._src[col.key] },
        });
    case 'person':
      return ({ row }) => {
        const name = row._src[col.key];
        const avatarUrl = col.avatarKey ? row._src[col.avatarKey] : undefined;
        const initials = col.initialsKey ? row._src[col.initialsKey] : undefined;
        return componentOutletConfigFactory(AssistantCellPersonComponent, {
          inputs: {
            name: typeof name === 'string' ? name : name == null ? '' : String(name),
            avatarUrl: typeof avatarUrl === 'string' ? avatarUrl : null,
            initials: typeof initials === 'string' ? initials : null,
          },
        });
      };
    default:
      return ({ row }) =>
        componentOutletConfigFactory(AssistantCellTextComponent, {
          inputs: { value: row._src[col.key] },
        });
  }
};

export const buildColumn = (
  col: ClientToolColumn,
  format: ClientToolFormatHint,
): NewDataTableColumn<AssistantTableRow> => ({
  id: col.key,
  label: col.label,
  sort: null,
  geometry: { width: parseWidth(col.width ?? undefined) },
  options: { sortable: true, draggable: false, resizable: false, removable: false },
  componentFactory: cellFactoryFor(col, format),
});
